import React from 'react';
import { Box, Typography, useMediaQuery, Grid, Container } from "@mui/material";
import { motion } from "framer-motion";
import {
    Security as SecurityIcon,
    Lightbulb as InnovationIcon,
    AccountBalance as IntegrityIcon,
    Groups as CollaborationIcon
} from "@mui/icons-material";
import selfRelianceImage from '../../assets/image/landing/self-reliance.jpg';
import innovationImage from '../../assets/image/landing/innovation.png';
import integrityImage from '../../assets/image/landing/integrity.png';
import collaborationImage from '../../assets/image/landing/collaboration.png';

const values = [
    {
        title: "Self-Reliance",
        description: "Focus on local production and innovation to strengthen national security capabilities.",
        imageUrl: selfRelianceImage,
        gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
        icon: <SecurityIcon />
    },
    {
        title: "Innovation",
        description: "Commit to continuous product development and technological advancement.",
        imageUrl: innovationImage,
        gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        icon: <InnovationIcon />
    },
    {
        title: "Integrity",
        description: "Uphold transparency, accountability, and ethics in all business operations.",
        imageUrl: integrityImage,
        gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        icon: <IntegrityIcon />
    },
    {
        title: "Collaboration",
        description: "Establish strong partnerships with local and international entities to deliver superior security solutions.",
        imageUrl: collaborationImage,
        gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
        icon: <CollaborationIcon />
    },
];

const CoreValuesSectionComponent = () => {
    const isSmallScreen = useMediaQuery('(max-width:900px)');
    const [hoveredIndex, setHoveredIndex] = React.useState(null);

    return (
        <Box sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 2, md: 10 },
            background: '#f2f2f2',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        {/* <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 3,
                            py: 1,
                            borderRadius: '50px',
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            mb: 4
                        }}>
                            <Typography sx={{
                                fontSize: '0.85rem',
                                fontWeight: '700',
                                color: '#3b82f6',
                                letterSpacing: '2.5px',
                                textTransform: 'uppercase'
                            }}>
                                Ethos & Principles
                            </Typography>
                        </Box> */}

                        <Typography variant="h2" sx={{
                            fontWeight: '900',
                            fontSize: { xs: 40, md: 64 },
                            color: '#08244d',
                            mb: 4,
                            letterSpacing: '-2.5px',
                            lineHeight: 1.1
                        }}>
                            Our <span style={{ color: '#3b82f6' }}>Core</span> Values
                        </Typography>

                        <Typography sx={{
                            color: '#475569',
                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                            mx: 'auto',
                            lineHeight: 1.7,
                            fontWeight: '400'
                        }}>
                            Driving national security through local innovation, ethical leadership,
                            and strategic global collaboration.
                        </Typography>
                    </motion.div>
                </Box>

                {/* Values Grid */}
                <Grid container spacing={2} alignItems="stretch">
                    {values.map((value, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.7,
                                    delay: index * 0.1,
                                    ease: [0.16, 1, 0.3, 1]
                                }}
                                style={{ height: '100%' }}
                            >
                                <Box
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    sx={{
                                        height: '100%',
                                        p: 5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        borderRadius: '32px',
                                        background: '#ffffff',
                                        border: '1px solid',
                                        borderColor: hoveredIndex === index
                                            ? 'rgba(59, 130, 246, 0.5)'
                                            : '#e2e8f0',
                                        transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                                        transform: hoveredIndex === index ? 'translateY(-10px)' : 'translateY(0)',
                                        boxShadow: hoveredIndex === index
                                            ? '0 30px 60px rgba(0,0,0,0.12)'
                                            : '0 4px 20px rgba(0,0,0,0.04)',
                                        overflow: 'hidden',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {/* Icon Header */}
                                    <Box sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '18px',
                                        background: hoveredIndex === index ? value.gradient : '#f8fafc',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: hoveredIndex === index ? '#ffffff' : '#3b82f6',
                                        mb: 4,
                                        transition: 'all 0.4s ease',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '2rem'
                                        }
                                    }}>
                                        {value.icon}
                                    </Box>

                                    {/* Title */}
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: '800',
                                            color: '#08244d',
                                            mb: 2,
                                            fontSize: '1.8rem',
                                            letterSpacing: '-0.5px',
                                            lineHeight: 1.2,
                                            textAlign: 'start'
                                        }}
                                    >
                                        {value.title}
                                    </Typography>

                                    {/* Description - flexGrow pushes image to bottom */}
                                    <Typography sx={{
                                        color: '#64748b',
                                        lineHeight: 1.7,
                                        fontSize: '1.2rem',
                                        mb: 5,
                                        flexGrow: 1,
                                        textAlign: 'left'
                                    }}>
                                        {value.description}
                                    </Typography>

                                    {/* Image Preview - Aligned at bottom */}
                                    <Box sx={{
                                        width: '100%',
                                        height: 220,
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        background: '#f8fafc',
                                        border: '1px solid #f1f5f9',
                                        mt: 'auto'
                                    }}>
                                        <Box
                                            component="img"
                                            src={value.imageUrl}
                                            alt={value.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                filter: hoveredIndex === index ? 'none' : 'grayscale(0.3)',
                                                transition: 'all 0.6s ease',
                                                transform: hoveredIndex === index ? 'scale(1.1)' : 'scale(1)'
                                            }}
                                        />
                                    </Box>

                                    {/* Corner Number */}
                                    <Typography sx={{
                                        position: 'absolute',
                                        top: 24,
                                        right: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: '800',
                                        color: '#cbd5e1',
                                        letterSpacing: '0.5px'
                                    }}>
                                        0{index + 1}
                                    </Typography>
                                </Box>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                {/* Bottom Stats */}
                <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e2e8f0' }}>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={6} md={3} sx={{ textAlign: 'center' }}>
                            <Typography sx={{ color: '#08244d', fontSize: '2.5rem', fontWeight: '900', mb: 1 }}>100%</Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '700' }}>Local Integrity</Typography>
                        </Grid>
                        <Grid item xs={6} md={3} sx={{ textAlign: 'center' }}>
                            <Typography sx={{ color: '#08244d', fontSize: '2.5rem', fontWeight: '900', mb: 1 }}>24/7</Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '700' }}>Innovation Cycle</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}

export default CoreValuesSectionComponent;
